import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { Button, Card, CardBody, FormFeedback, FormGroup, Tooltip, UncontrolledTooltip } from 'reactstrap';
import { getAcreedor } from 'api/actions/blockchain';
import { fetcher } from 'api/client';
import { useSignState } from 'context/signview';
import useMetamask from 'hooks/useMetamask';
import EndorsePagare from 'components/Modals/EndorsePagare';
import TratoLoader from 'components/Shared/TratoLoader';
import { toast } from 'react-toastify';

export default function Pagare() {

    const { contract, jwt } = useSignState();
    const { data: { acreedor } = {}, isLoading: loadingAcreedor } = useQuery(['contract-acreedor'], () => fetcher(getAcreedor(contract.pagareId), jwt), {
        refetchOnWindowFocus: false,
        enabled: Boolean(contract.isPagare && contract.pagareId)
    });

    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const toggle = () => {
        setIsOpen(!isOpen);
    }

    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries(['contract-acreedor']);
    }, [contract.blockchainConfirmed])

    const handleGoToContract = () => {
        window.open(`${process.env.REACT_APP_ETHERSCAN_URL}/token/${process.env.REACT_APP_PAGARE_ADDRESS}?a=${contract.pagareId}`, '_blank');
    }

    const { providerAddress, burnToken, isEndorsing } = useMetamask(process.env.REACT_APP_PAGARE_ADDRESS, contract._id, contract.pagareId, jwt);

    const isPagareOwner = acreedor?.toLowerCase() === providerAddress?.toLowerCase();

    const burnPagare = () => {
        burnToken(contract.pagareId).then((mutation) => {
            if (mutation) {
                toast.success(t('blockchain.endorsed'));
            }
        }).catch((error) => {
            toast.error(t(`errors.${error.message}`))
        })
    }

    return (
        <div id="pagare">
            <EndorsePagare
                isOpen={isOpen}
                toggle={toggle}
                pagareId={contract?.pagareId}
                contractId={contract?._id}
                jwt={jwt}
                acreedor={acreedor}
            />

            <h4><b>{t('pagare.title')}</b></h4>
            <span className="text-justify">{t('pagare.instructions')}</span>
            <hr />

            <div hidden={!contract.pagareId}>
                <h4 className="mt-4">{t('pagare.id')}</h4>
                <div className="text-center">
                    <span
                        className="bg-light px-3 py-2 rounded-lg text-muted pointer"
                        onClick={handleGoToContract}
                    >
                        <b>{contract.pagareId}</b>
                    </span>
                </div>
                <hr />
            </div>

            <h4 className="mt-4">{t('pagare.address')}</h4>
            <div className="text-center">
                <Card className="rounded-lg pointer" onClick={handleGoToContract}>
                    <CardBody>
                        <div className="mb-3">
                            <img
                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJQAAACUCAYAAAB1PADUAAAAAklEQVR4AewaftIAAATeSURBVO3BQY4jRxAEwfAC//9l1xzzVECjk6PVKszwR6qWnFQtOqladFK16KRq0UnVopOqRSdVi06qFp1ULTqpWnRSteikatFJ1aKTqkUnVYs+eQnIb1JzA2RSMwG5UTMBeULNBGRS8wSQ36TmjZOqRSdVi06qFn2yTM0mIE+omYBMat5Q84SaCciNmhs1m4BsOqladFK16KRq0SdfBuQJNU8AmdS8AWRSMwGZ1ExAJjWTmk1AnlDzTSdVi06qFp1ULfrkL6fmCTUTkEnNjZoJyKRmAjKp+S87qVp0UrXopGrRJ385IDdqngAyqZmA/J+dVC06qVp0UrXoky9T85uATGreUHMDZFLzhJo31PxJTqoWnVQtOqla9MkyIP8mNROQSc0EZFIzAZnUPAFkUjMBmdTcAPmTnVQtOqladFK1CH/kPwzIpOYGyKRmAvKEmgnIpOb/5KRq0UnVopOqRfgjLwCZ1ExANql5AsgTam6ATGpugExqJiCTmgnIJjXfdFK16KRq0UnVok9+mZoJyKTmBsgTam6AvAHkRs0mNROQGzUTkBs1b5xULTqpWnRSteiTZUAmNROQJ4A8oWYCcqPmBsik5gkgk5pJzY2aCcgbar7ppGrRSdWik6pF+CP/IiCTmhsgk5oJyKRmAvKb1DwB5EbNBGRScwNkUrPppGrRSdWik6pF+CMvAPmbqHkCyKRmAvKGmieA3Kj5ppOqRSdVi06qFn2yTM0NkBs13wTkBsikZgLyhJobIJOaCcgTap4AMql546Rq0UnVopOqRZ8sAzKpuVFzA2RSMwF5Qs0EZFIzAZnUPAFkUjOpmYBsAnKjZtNJ1aKTqkUnVYvwRxYBmdTcALlRcwNkUnMDZFIzAZnUTEA2qXkDyI2aCcikZtNJ1aKTqkUnVYvwR14AMqmZgNyomYBsUvMGkCfUvAFkUvMGkCfUvHFSteikatFJ1aJPlgGZ1NwAuVEzAblRMwGZ1ExAvgnIpGYCMqmZgLyh5gbIppOqRSdVi06qFn2yTM0NkBs1E5BJzQRkAjKpuVFzA2RS8waQJ9TcAHlDzaaTqkUnVYtOqhZ98ocBMqmZgExqJiA3QN4AsknNBGRSMwF5A8ikZtNJ1aKTqkUnVYs++ZepmYBMQCY1N2omIJOaCcik5gbIjZobIBOQGyBPqJmA/KaTqkUnVYtOqhbhj/yHAZnUvAFkUjMBmdRMQCY1E5AbNU8AuVFzA2RS88ZJ1aKTqkUnVYs+eQnIb1IzqZmA3KiZgExqbtRMQCY1N2omIDdAJjU3aiYgN2o2nVQtOqladFK16JNlajYBuQEyqZmATEAmNU8AuQFyo+YJNU8AmdRMQL7ppGrRSdWik6pFn3wZkCfUbFKzSc0EZFLzBpA31ExAftNJ1aKTqkUnVYs++Z8BMql5AsgmNZuATGp+00nVopOqRSdViz75ywCZ1ExqJiCTmhs1TwCZ1NwAeUPNDZBJzaaTqkUnVYtOqhZ98mVqvknNBOQGyKTmCSCTmgnIpGYCMqm5UXMD5E9yUrXopGrRSdUi/JEXgPwmNROQJ9RMQCY1m4BMam6A3KiZgDyh5ptOqhadVC06qVqEP1K15KRq0UnVopOqRSdVi06qFp1ULTqpWnRSteikatFJ1aKTqkUnVYtOqhadVC06qVr0D1zmVB5YZ6qdAAAAAElFTkSuQmCC"
                                alt=""
                            />
                            <small
                                className="bg-orange-light px-3 py-2 text-primary rounded-lg text-truncate"
                            >
                                {process.env.REACT_APP_PAGARE_ADDRESS}
                            </small>
                        </div>
                    </CardBody>
                </Card>
            </div>
            <hr />

            <div className="text-center">
                {loadingAcreedor && <TratoLoader size="lg" className="mt-2" />}
            </div>

            <div hidden={!acreedor}>
                <h4 className="mt-4">{t('pagare.creditor_address')}</h4>
                <div className="text-center">
                    <a
                        target="_blank"
                        className="bg-light px-3 py-2 rounded-lg text-muted"
                        href={`${process.env.REACT_APP_ETHERSCAN_URL}/address/${acreedor}#tokentxnsErc721`}
                        rel="noreferrer"
                    >
                        {acreedor}
                    </a>
                </div>
                <hr />

                <h4 className="mt-4">{t('pagare.accions')}</h4>
                <div >
                    <FormGroup className="text-center">
                        <Button
                            id="endorse-button"
                            name="endorse-button"
                            block
                            outline
                            onClick={toggle}
                            disabled={!isPagareOwner || !contract.blockchainConfirmed || isEndorsing}
                        >
                            {contract.blockchainConfirmed ?
                                t('pagare.endorse') :
                                (
                                    <small>
                                        <i className="fas fa-cubes fa-spin"></i>{' '}
                                        {t('blockchain.uncommited_tx')}
                                    </small>
                                )}
                        </Button>
                        {!isPagareOwner && <small className="text-muted">{t('pagare.creditor_only')}</small>}
                    </FormGroup>
                    <FormGroup className="text-center">
                        <Button
                            id="cancel-button"
                            name="cancel-button"
                            block
                            color="danger"
                            outline
                            onClick={burnPagare}
                            disabled={!isPagareOwner || !contract.blockchainConfirmed || isEndorsing}
                        >
                            {t('pagare.cancel')}
                        </Button>
                        {!isPagareOwner && <small className="text-muted">{t('pagare.creditor_only')}</small>}
                    </FormGroup>
                </div>

            </div>
        </div>
    );
}